import { Injectable } from '@angular/core';
import { assert } from '@sama/js-core';
import { map, skip } from 'rxjs/operators';
import { MeasureToolMode } from '../../../cross-team-shared/types/tool-modes';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import { deleteIfDefined } from '../../state/render-state';
import { AbstractPointCloudTool } from '../abstract-point-cloud-tool';
import { FreePointToolHelper } from './free-point-tool-helper';
import { MeasureSecondaryToolbar3DComponent } from './measure-secondary-toolbar.component';
import { subpointHeightToolHelper } from './subpoint-height-tool-helper';

export enum MeasureToolState {
  Waiting = 0,
  InProgress,
  Blocked,
}

/**
 * Tool responsible to calculate the distance between two points inside the
 * point cloud. The Measure tool listens to mouse events to orchestrate when
 * visual elements should be displayed and when to calculate distances.
 */

@Injectable()
export class MeasureTool extends AbstractPointCloudTool {
  readonly name = 'Measure';

  override secondaryToolbarType = MeasureSecondaryToolbar3DComponent;

  private freePointHelper?: FreePointToolHelper;
  private subpointHeightHelper?: subpointHeightToolHelper;
  private mode: MeasureToolMode = 'free-point';

  override async onActivate() {
    await super.onActivate();
    this.shellConnector.modifyShapeSelection([]);

    this.onEvent('container', 'click', this.onContainerClick, {
      evenWithRegisteredHelper: true,
    });

    this.subscribeToWhileActive(
      this.store('render')('frameNumber').$.pipe(
        skip(1), // skip the current frame emission
        map(() => ({ source: 'scrub_frame' })),
      ),
      this.onCancel,
    );
  }

  override onCancel(eventTrackingValue: EventTrackingValue) {
    super.onCancel(eventTrackingValue);
    this.freePointHelper?.cancel(eventTrackingValue);
    this.cleanUp();
    this.shellConnector.trackEvent({
      id: 'cancel_shape',
      bucket: 'annotation',
      ...eventTrackingValue,
    });
  }

  getMeasureMode() {
    return this.mode;
  }

  setMeasureMode(mode: MeasureToolMode) {
    this.cleanUp();
    this.mode = mode;
    this.setSelectedTool();
  }

  override setMode(mode: string) {
    assert(mode === 'free-point' || mode === 'subpoint-height', 'invalid mode');
    this.setMeasureMode(mode);
  }

  protected override setSelectedTool() {
    this.shellConnector.setSelectedTool(this.name, this.getMeasureMode());
  }

  private onContainerClick() {
    if (this.mode === 'free-point') {
      this.handleFreePointClick();
    } else {
      this.handlesubpointHeightClick();
    }
  }

  private handleFreePointClick() {
    if (!this.freePointHelper) {
      this.freePointHelper = new FreePointToolHelper(this.injector);
      this.freePointHelper.startListening();
      void this.freePointHelper.completion.finally(() => this.cleanUp());
    }

    this.freePointHelper.handleClick();
  }

  private handlesubpointHeightClick() {
    if (!this.subpointHeightHelper) {
      this.subpointHeightHelper = new subpointHeightToolHelper(this.injector);
      this.subpointHeightHelper.startListening();
      void this.subpointHeightHelper.completion.finally(() =>
        this.cleanUp(false),
      );
    }

    this.subpointHeightHelper.handleClick();
  }

  private cleanUp(shouldStopListening = true) {
    if (shouldStopListening) {
      void this.freePointHelper?.stopListening();
      void this.subpointHeightHelper?.stopListening();
    }
    this.freePointHelper = undefined;
    this.subpointHeightHelper = undefined;
    this.store('render')('measurementMode').setUsing(deleteIfDefined);
  }
}
